import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js'
import { Sky } from 'three/addons/objects/Sky.js'
import { Timer } from 'three/addons/misc/Timer.js'
import GUI from 'lil-gui'
import { Mesh, MeshStandardMaterial } from 'three/webgpu'
import gsap from 'gsap'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'
import { Text, preloadFont  } from 'troika-three-text'
import { vittime as vittimeIT } from './vittime-it.js'
import { vittime as vittimeEN } from './vittime-en.js'
import { t, getUserLanguage } from './i18n.js'
import {FontLoader} from 'three/examples/jsm/loaders/FontLoader.js'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js'


/**
 * Base
 */
// Debug
const gui = new GUI()
gui.hide()

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()


const isMobile = (() => {
    const userAgent = navigator.userAgent.toLowerCase()
    const mobileKeywords = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i
    const isUserAgentMobile = mobileKeywords.test(userAgent)
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    const isSmallScreen = window.innerWidth < 768
    const isIPad = /ipad/i.test(userAgent) || 
                   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    return isUserAgentMobile || (isTouchDevice && isSmallScreen) || isIPad
})()


const config = {
    isMobile: isMobile,
    maxGraves: isMobile ? 30 : 100,
    shadowsEnabled: !isMobile,
    floorSegments: isMobile ? 20 : 80,
    doorSegments: isMobile ? 10 : 80,
    bushSubdivisions: isMobile ? 1 : 8,
    ghostSegments: isMobile ? 16 : 32, 
    pixelRatio: isMobile ? 1 : Math.min(window.devicePixelRatio, 2),
    fogDensity: isMobile ? 0.15 : 0.22, 
    antialias: !isMobile,
    textCurveSegments: isMobile ? 4 : 12,  //12
    textBevelSegments: isMobile ? 2 : 6,  //6
}

//console.log("isMobile", isMobile)

const currentLang = getUserLanguage()
const translate = (key) => t(key, currentLang)
const vittime = currentLang === 'it' ? vittimeIT : vittimeEN


/*
const axesHelper = new THREE.AxesHelper(10)
scene.add(axesHelper)
*/


/* 
    Textures
*/ 
const textureLoader = new THREE.TextureLoader()

//Floor
const floorAlphaTexture = textureLoader.load('./floor/alpha.webp')
const floorColorTexture = textureLoader.load('./floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_diff_1k.webp')
const floorARMTexture = textureLoader.load('./floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_arm_1k.webp')
const floorNormalTexture = textureLoader.load('./floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_nor_gl_1k.webp')
const floorDisplacementTexture = textureLoader.load('./floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_disp_1k.webp')

floorColorTexture.colorSpace = THREE.SRGBColorSpace

floorColorTexture.repeat.set(8,8)
floorARMTexture.repeat.set(8,8)
floorNormalTexture.repeat.set(8,8)
floorDisplacementTexture.repeat.set(8,8)

floorColorTexture.wrapS = THREE.RepeatWrapping
floorARMTexture.wrapS = THREE.RepeatWrapping
floorNormalTexture.wrapS = THREE.RepeatWrapping
floorDisplacementTexture.wrapS = THREE.RepeatWrapping

floorColorTexture.wrapT = THREE.RepeatWrapping
floorARMTexture.wrapT = THREE.RepeatWrapping
floorNormalTexture.wrapT = THREE.RepeatWrapping
floorDisplacementTexture.wrapT = THREE.RepeatWrapping


// Wall
const wallColorTexture = textureLoader.load('./wall/castle_brick_broken_06_1k/castle_brick_broken_06_diff_1k.webp')
const wallARMTexture = textureLoader.load('./wall/castle_brick_broken_06_1k/castle_brick_broken_06_arm_1k.webp')
const wallNormalTexture = textureLoader.load('./wall/castle_brick_broken_06_1k/castle_brick_broken_06_nor_gl_1k.webp')

wallColorTexture.colorSpace = THREE.SRGBColorSpace

// Roof
const roofColorTexture = textureLoader.load('./roof/roof_slates_02_1k/roof_slates_02_diff_1k.webp')
const roofARMTexture = textureLoader.load('./roof/roof_slates_02_1k/roof_slates_02_arm_1k.webp')
const roofNormalTexture = textureLoader.load('./roof/roof_slates_02_1k/roof_slates_02_nor_gl_1k.webp')

roofColorTexture.colorSpace = THREE.SRGBColorSpace

roofColorTexture.repeat.set(3, 1)
roofARMTexture.repeat.set(3, 1)
roofNormalTexture.repeat.set(3, 1)

roofColorTexture.wrapS = THREE.RepeatWrapping
roofARMTexture.wrapS = THREE.RepeatWrapping
roofNormalTexture.wrapS = THREE.RepeatWrapping



// Bush
const bushColorTexture = textureLoader.load('./bush/forest_leaves_03_1k/forest_leaves_03_diff_1k.webp')
const bushARMTexture = textureLoader.load('./bush/forest_leaves_03_1k/forest_leaves_03_arm_1k.webp')
const bushNormalTexture = textureLoader.load('./bush/forest_leaves_03_1k/forest_leaves_03_nor_gl_1k.webp')
const bushDisplacementTexture = textureLoader.load('./bush/forest_leaves_03_1k/forest_leaves_03_disp_1k.webp')


bushColorTexture.colorSpace = THREE.SRGBColorSpace

bushColorTexture.repeat.set(2, 1)
bushARMTexture.repeat.set(2, 1)
bushNormalTexture.repeat.set(2, 1)
bushDisplacementTexture.repeat.set(2, 1)

bushColorTexture.wrapS = THREE.RepeatWrapping
bushARMTexture.wrapS = THREE.RepeatWrapping
bushNormalTexture.wrapS = THREE.RepeatWrapping
bushDisplacementTexture.wrapS = THREE.RepeatWrapping


// Grave
const graveColorTexture = textureLoader.load('./grave/plastered_stone_wall_1k/plastered_stone_wall_diff_1k.webp')
const graveARMTexture = textureLoader.load('./grave/plastered_stone_wall_1k/plastered_stone_wall_arm_1k.webp')
const graveNormalTexture = textureLoader.load('./grave/plastered_stone_wall_1k/plastered_stone_wall_nor_gl_1k.webp')

graveColorTexture.colorSpace = THREE.SRGBColorSpace

graveColorTexture.repeat.set(0.3, 0.4)
graveARMTexture.repeat.set(0.3, 0.4)
graveNormalTexture.repeat.set(0.3, 0.4)


//Door
const doorColorTexture = textureLoader.load('./door/color.webp')
const doorAlphaTexture = textureLoader.load('./door/alpha.webp')
const doorAOTexture = textureLoader.load('./door/ambientOcclusion.webp')
const doorRoughnessTexture = textureLoader.load('./door/roughness.webp')
const doorMetalnessTexture = textureLoader.load('./door/metalness.webp')
const doorNormalTexture = textureLoader.load('./door/normal.webp')
const doorDisplacementTexture = textureLoader.load('./door/height.webp')

doorColorTexture.colorSpace = THREE.SRGBColorSpace


//Targhetta Dexter
const targhettaDexterColorTexture = textureLoader.load('./targhetta-dexter/min/targhetta-dexter-color.webp')
const targhettaDexterAlphaTexture = textureLoader.load('./targhetta-dexter/min/targhetta-dexter-alpha.webp')

targhettaDexterColorTexture.colorSpace = THREE.SRGBColorSpace


//Blood
const bloodColorTexture = textureLoader.load('./blood/blood_streaks_32_88_diffuse.jpg')
const bloodAOTexture = textureLoader.load('./blood/blood_streaks_32_88_ao.jpg')
const bloodRoughnessTexture = textureLoader.load('./blood/blood_streaks_32_88_roughness.jpg')
const bloodMetalnessTexture = textureLoader.load('./blood/blood_streaks_32_88_metalness.jpg')
const bloodNormalTexture = textureLoader.load('./blood/blood_streaks_32_88_normal.jpg')

bloodColorTexture.colorSpace = THREE.SRGBColorSpace

let boundingBox = null

// Fonts
const fontLoader = new FontLoader()
fontLoader.load(
    './fonts/Bloody_Normal.json',
    (font)=>{

        const textMaterial = new THREE.MeshStandardMaterial({
            //color:0xff0000
            map: bloodColorTexture,
            aoMap: bloodAOTexture,
            normalMap: bloodNormalTexture,
            metalnessMap: bloodMetalnessTexture,
            roughnessMap: bloodRoughnessTexture,
            wireframe: false,

        })

        const textGeometry = new TextGeometry(
            'DEXTER',
            {
                font: font,
                size: 0.9,
                depth: 0.2,
                curveSegments: config.textCurveSegments, //12
                bevelEnabled: true,
                bevelThickness: 0.03,//0.03,
                bevelSize: 0.02,//0.02,
                bevelOffset: 0,
                bevelSegments: config.textBevelSegments //6
            }
        )

        textGeometry.center()
        textGeometry.computeBoundingBox()

        const mainText = new THREE.Mesh(textGeometry, textMaterial)
        mainText.name = 'mainText'
        house.add(mainText)

        mainText.position.y = 3.8
        mainText.position.z = 1.3

        const halfWallSize = 2.2; 
        const wallHeight = 4.3;

        boundingBox = new THREE.Box3(
            new THREE.Vector3(-halfWallSize, 0, -halfWallSize),  // Min
            new THREE.Vector3(halfWallSize, wallHeight, halfWallSize)  
        );

        // Helper per visualizzare il bounding box
        // const boxHelper = new THREE.Box3Helper(boundingBox, 0xff0000);  // Rosso
        // scene.add(boxHelper);
    }

)




/**
 * House
 */

// Floor 
const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(20, 20, config.floorSegments, config.floorSegments),
    new THREE.MeshStandardMaterial({
        wireframe: false,
        alphaMap: floorAlphaTexture,
        transparent: true,
        map: floorColorTexture,
        aoMap: floorARMTexture,
        roughnessMap: floorARMTexture,
        metalnessMap: floorARMTexture,
        normalMap: floorNormalTexture,
        displacementMap: floorDisplacementTexture,
        displacementScale: 0.4,
        displacementBias: - 0.2
    })
)

gui.add(floor.material, 'displacementScale').min(0).max(1).step(0.001).name('Floor displacementScale')
gui.add(floor.material, 'displacementBias').min(-1).max(1).step(0.001).name('Floor displacementBias')

floor.rotation.x = - Math.PI * 0.5
scene.add(floor)

// House container
const house = new THREE.Group()
scene.add(house)


// Walls
const walls = new THREE.Mesh(
    new THREE.BoxGeometry(4, 2.5, 4),
    new THREE.MeshStandardMaterial({
        map: wallColorTexture,
        aoMap: wallARMTexture,
        roughnessMap: wallARMTexture,
        metalnessMap: wallARMTexture,
        normalMap: wallNormalTexture,
    })
)

walls.position.y += 1.25 
house.add(walls)

// Roof
const roof = new THREE.Mesh(
    new THREE.ConeGeometry(3.5, 1.5, 4),
    new THREE.MeshStandardMaterial({
        map: roofColorTexture,
        aoMap: roofARMTexture,
        roughnessMap: roofARMTexture,
        metalnessMap: roofARMTexture,
        normalMap: roofNormalTexture,
    })
)
roof.position.y = 2.5 + 0.75 
roof.rotation.y = Math.PI * 0.25
house.add(roof)

// Door
const door = new THREE.Mesh(
    new THREE.PlaneGeometry(2.2, 2.2, config.doorSegments, config.doorSegments),
    new THREE.MeshStandardMaterial({
        //color:0xff0000,
        map: doorColorTexture,
        transparent: true,
        alphaMap: doorAlphaTexture,
        aoMap: doorAOTexture,
        displacementMap:doorDisplacementTexture,
        displacementScale: 0.15,
        displacementBias: - 0.04,
        normalMap: doorNormalTexture,
        metalnessMap: doorMetalnessTexture,
        roughnessMap: doorRoughnessTexture,
    })
)
door.position.y = 1
door.position.z = 2 + 0.045
house.add(door)

// Targhetta Dexter
const targhettaDexter = new THREE.Mesh(
    new THREE.PlaneGeometry(0.3, 0.3),
    new THREE.MeshStandardMaterial({
        map: targhettaDexterColorTexture,
        transparent: true,
        alphaMap: targhettaDexterAlphaTexture,
        metalness: 0.57,
        roughness: 0.12
    })
)
targhettaDexter.position.z = 2.12 //2.075
targhettaDexter.position.y = 1.5
house.add(targhettaDexter)

gui.add(targhettaDexter.material, 'metalness').min(0).max(2).step(0.01).name('targhettaDexter metalness')
gui.add(targhettaDexter.material, 'roughness').min(0).max(2).step(0.01).name('targhettaDexter roughness')

// Bushes
const bushGeometry = new THREE.DodecahedronGeometry(1, config.bushSubdivisions)

const bushMaterial = new THREE.MeshStandardMaterial({
        //color: 0xe1a900,
        wireframe: false,
        map: bushColorTexture,
        aoMap: bushARMTexture,
        roughnessMap: bushARMTexture,
        metalnessMap: bushARMTexture,
        normalMap: bushNormalTexture,
        displacementMap: bushDisplacementTexture,
        displacementScale: 0.4,
        
    })

const bush1 = new Mesh( bushGeometry, bushMaterial)
bush1.scale.set(0.5, 0.5, 0.5)
bush1.position.set(1.2, 0.2, 2.2)
bush1.rotation.x = - 0.75

const bush2 = new Mesh( bushGeometry, bushMaterial)
bush2.scale.set(0.25, 0.25, 0.25)
bush2.position.set(1.8, 0.1, 2.1)
bush2.rotation.x = - 0.75

const bush3 = new Mesh( bushGeometry, bushMaterial)
bush3.scale.set(0.4, 0.4, 0.4)
bush3.position.set(-1.0, 0.1, 2.2)
bush3.rotation.x = - 0.75

const bush4 = new Mesh( bushGeometry, bushMaterial)
bush4.scale.set(0.15, 0.15, 0.15)
bush4.position.set(-1.2, 0.05, 2.6)
bush4.rotation.x = - 0.75



house.add(bush1, bush2, bush3, bush4)

// Graves
let graveGeometry=null

if(config.isMobile){
    graveGeometry=new THREE.BoxGeometry(0.6, 0.8, 0.2)
}else{
    graveGeometry = new RoundedBoxGeometry(0.6,0.8,0.2,2,0.05)
}

const graveMaterial=new MeshStandardMaterial({
    map: graveColorTexture,
    aoMap: graveARMTexture,
    roughnessMap: graveARMTexture,
    metalnessMap: graveARMTexture,
    normalMap: graveNormalTexture,
})

const graves = new THREE.Group()

let fontReady = false
const FONT_URL = 'https://fonts.gstatic.com/s/oldstandardtt/v18/MwQubh3o1vLImiwAVvYawgcf2eVurVC5RHdCZg.ttf'

preloadFont(
  {
    font: FONT_URL,
    characters: 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789àèéìòùç ()-.,:;\''
  },
  () => {
    fontReady = true

    createGraves()
    
    // Timeline
    const introCameraTL = gsap.timeline({
        onComplete: () => {
            if(config.isMobile){
                controls.enabled = true
            }
            if(instructions){
                instructions.style.display = 'block' 
            }
        }
    })
    
    //POSIZIONE CAMERA FINALE
    introCameraTL.to(camera.position, {
        x: 1.03,
        y: 1.93,
        z: 4.71,
        duration: 7,
        ease: "power4.in"
    })
    
  }
)

const sharedTextMaterial = new THREE.MeshBasicMaterial({
    color: 0xf0bb6e,
})


const createText = (testoDaScrivere, fontSize, yOffset, grave)=>{
    const text = new Text()
    text.font = FONT_URL
    text.text = testoDaScrivere
    text.fontSize = fontSize
    text.maxWidth = 0.4 
    text.textAlign = 'center'
    text.anchorX = 'center'
    text.anchorY = 'middle'
    text.material = sharedTextMaterial
    text.position.set(0, yOffset, 0.103)
    text.sync()
    grave.add(text)
}


const shuffleArray = (array) => {
    const shuffled = [...array]  
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
}



const createGraves = ()=>{

    const vittimeShuffle = shuffleArray(vittime)

    for(let i = 0; i < Math.min(vittimeShuffle.length, config.maxGraves); i++){
        const angle = Math.random() * Math.PI * 2
        const radius = 3 + Math.random() * 4
        const x = Math.sin(angle) * radius
        const z = Math.cos(angle) * radius

        const grave= new THREE.Mesh(graveGeometry, graveMaterial)
        grave.position.x = x
        grave.position.z = z
        grave.position.y = Math.random() * 0.4

        grave.rotation.x =  (Math.random() - 0.5) * 0.4
        grave.rotation.y =  (Math.random() - 0.5) * 0.4
        grave.rotation.z =  (Math.random() - 0.5) * 0.4

        graves.add(grave)

        grave.castShadow = true
        grave.receiveShadow = true

        createText(translate('season') + " " + vittimeShuffle[i].stagione, 0.015, 0.30, grave)
        createText(vittimeShuffle[i].nome,  0.03, 0.24, grave)
        createText(vittimeShuffle[i].descrizione,  0.023, 0.1, grave) 
        
    }

    scene.add(graves)
}



// Lampadina porta
const lampadinaPorta = new Mesh(
    new THREE.SphereGeometry(0.04, 8, 8),
    new THREE.MeshStandardMaterial({
        color: 0xfff0ea,
        emissive: 0xfff0ea,
        emissiveIntensity: 3,
        roughness: 0.2,
        metalness: 0
    })
)
lampadinaPorta.position.z = 2.2
lampadinaPorta.position.y = 2.47
house.add(lampadinaPorta)


// Ghost siluette
const createGhost = (ghostColor)=>{
    const group = new THREE.Group()
    const ghostMaterialHead = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        transparent: false,
        opacity: 0.5,
        roughness: 0.8,
        metalness: 0,
        emissive: ghostColor,
        emissiveIntensity: 0.4
    })
    const ghostMaterialBody = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.5,
        roughness: 0.1,
        metalness: 0,
        emissive: ghostColor,
        emissiveIntensity: 0.4
    })
    const head = new THREE.Mesh(
        new THREE.SphereGeometry(0.25, config.ghostSegments, config.ghostSegments),
        ghostMaterialHead
    )
    head.position.y = 0.5 + 0.3
    const body = new THREE.Mesh(
        new THREE.CylinderGeometry(0.20, 0.3, 0.6, config.ghostSegments),
        ghostMaterialBody
    )
    body.position.y = 0.05 + 0.3
    group.add(head, body)

    gsap.to(group.rotation, {
        z: () => "+=" + ( Math.random() - 0.5) * Math.PI * 0.1,
        x: () => "+=" + ( Math.random() - 0.5) * Math.PI * 0.1,
        duration: (Math.random() + 1) * 1.2,
        repeat: -1,
        yoyo: true,
        ease: "elastic.out(1,0.3)",
        //repeatRefresh: true,
    })


    return group
}

//GHOST 1
const ghostSiluette1 = createGhost(0x8800ff)
scene.add(ghostSiluette1)

//GHOST 2
const ghostSiluette2 = createGhost(0xff0088)
scene.add(ghostSiluette2)

//GHOST 3
const ghostSiluette3 = createGhost(0xff0000)
scene.add(ghostSiluette3)





/**
 * Lights
 */
// Ambient light
const ambientLight = new THREE.AmbientLight('#86cdff', 0.275)
scene.add(ambientLight)

// Directional light
const directionalLight = new THREE.DirectionalLight('#86cdff', 1.0)
directionalLight.position.set(3, 2, -8)
scene.add(directionalLight)
//const directionalLightHelper = new THREE.DirectionalLightHelper(directionalLight)
//scene.add(directionalLightHelper)


// Door light
const doorLight= new THREE.PointLight(0xff7d46, 5)
house.add(doorLight)
// const doorLightHelper = new THREE.PointLightHelper(doorLight, 0.5)
// house.add(doorLightHelper)
doorLight.position.z = 2.2
doorLight.position.y = 2.47

/*
    Ghosts
*/
const ghost1=new THREE.PointLight(0x8800ff, 12)
const ghost2=new THREE.PointLight(0xff0088, 12)
const ghost3=new THREE.PointLight(0xff0000, 12)
scene.add(ghost1, ghost2, ghost3)

/*
const ghost1Helper=new THREE.PointLightHelper(ghost1)
const ghost2Helper=new THREE.PointLightHelper(ghost2)
const ghost3Helper=new THREE.PointLightHelper(ghost3)
scene.add(ghost1Helper, ghost2Helper, ghost3Helper)
*/



/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(config.pixelRatio)
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)

scene.add(camera)

// Controls

let controls=null
let instructions = null 

if(config.isMobile){
    // Mobile: OrbitControls
    controls = new OrbitControls(camera, canvas)
    controls.enableDamping = true
    controls.minDistance = 2.9
    controls.maxDistance = 43
    controls.enablePan = false
    controls.maxPolarAngle = Math.PI * 0.48

    instructions = document.createElement('div')
    instructions.innerHTML = `
        <div class="instructions-panel mobile-panel">
            <h1 class="main-title">${translate('mainTitle')}</h1>
            <p class="main-description">${translate('mainDescription')}</p>
            <p class="footer-info">
                ${translate('mobileFooterInfo')}
            </p>
            <input type="button" class="btn-start" value="${translate('btnStart')}"/>
        </div>
    `

    document.body.appendChild(instructions)
    instructions.style.display = 'none'

    const btnStart=document.querySelector('.btn-start')

    // Click per attivare
    btnStart.addEventListener('click', () => {
        instructions.style.display = 'none'
    })

}else{
    // Desktop: PointerLockControls
    controls = new PointerLockControls(camera, canvas)

    instructions = document.createElement('div')
    instructions.innerHTML = `
        <div class="instructions-panel">
            <h1 class="main-title">${translate('mainTitle')}</h1>
            <p class="main-description">${translate('mainDescription')}</p>
            <div class="main-info">
                <strong class="subtitle">${translate('controlsTitle')}</strong>
                <div class="instruction-line">${translate('controlsArrowUpDown')}</div>
                <div class="instruction-line">${translate('controlsArrowLeftRight')}</div>
                <div class="instruction-line">${translate('controlsShift')}</div>
                <div class="instruction-line">${translate('controlsCtrl')}</div>
                <div class="instruction-line">${translate('controlsMouse')}</div>
            </div>
            <p class="footer-info">
                ${translate('footerEsc')}
            </p>
            <p class="main-description">${translate('footerEnter')}</p>
        </div>
    `

    document.body.appendChild(instructions)
    instructions.style.display = 'none'

    // Listener tastiera per INVIO
    const handleEnterKey = (event) => {
        if (event.code === 'Enter' && instructions.style.display === 'block') {
            controls.lock()
        }
    }
    document.addEventListener('keydown', handleEnterKey)

    // Eventi lock/unlock
    controls.addEventListener('lock', () => {
        instructions.style.display = 'none'
        document.removeEventListener('keydown', handleEnterKey)
    })

    controls.addEventListener('unlock', () => {
        instructions.style.display = 'block'
        document.addEventListener('keydown', handleEnterKey)
    })

    // Gestione rotazione mouse
    document.addEventListener('mousemove', (e) => {
        if (controls.isLocked) {
            const movementX = e.movementX || 0
            const movementY = e.movementY || 0
            euler.setFromQuaternion(camera.quaternion)
            euler.y -= movementX * 0.002
            euler.x -= movementY * 0.002
            euler.x = Math.max(PI_2 - maxPolarAngle, Math.min(PI_2 - minPolarAngle, euler.x))
            camera.quaternion.setFromEuler(euler)
        }
    })

}


//CAMERA INIZIALE

camera.position.x = -14.06
camera.position.y = 2.81
camera.position.z = 42.42

camera.rotation.x = -0.07
camera.rotation.y = -0.32
camera.rotation.z = -0.02

// Disabilita controlli temporaneamente
controls.enabled = false


/**
 * Renderer
 */

const renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: config.antialias })
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(config.pixelRatio)

/*
    Shadows
*/
// Renderer
renderer.shadowMap.enabled = config.shadowsEnabled
renderer.shadowMap.type = THREE.PCFSoftShadowMap

// Cast and receive
directionalLight.castShadow = true
doorLight.castShadow = true
ghost1.castShadow = true
ghost2.castShadow = true
ghost3.castShadow = true

walls.castShadow = true
walls.receiveShadow = true
roof.castShadow = true
floor.receiveShadow = true
door.receiveShadow = true

bush1.castShadow = true
bush1.receiveShadow = true
bush2.castShadow = true
bush2.receiveShadow = true
bush3.castShadow = true
bush3.receiveShadow = true
bush4.castShadow = true
bush4.receiveShadow = true


targhettaDexter.receiveShadow = true


//Shadow Camera Mapping
//directionalLight
directionalLight.shadow.mapSize.width = 256
directionalLight.shadow.mapSize.height = 256
directionalLight.shadow.camera.top = 8
directionalLight.shadow.camera.right = 8
directionalLight.shadow.camera.bottom = - 8
directionalLight.shadow.camera.left = - 8
directionalLight.shadow.camera.near = 2 
directionalLight.shadow.camera.far = 20 
/*
const directionLightCameraHelper = new THREE.CameraHelper(directionalLight.shadow.camera)
scene.add(directionLightCameraHelper)
*/


//Ghost1
ghost1.shadow.mapSize.width = 256
ghost1.shadow.mapSize.height = 256
ghost1.shadow.camera.far = 8 //10
//const ghost1CameraHelper = new THREE.CameraHelper(ghost1.shadow.camera)
//scene.add(ghost1CameraHelper)

//Ghost2
ghost2.shadow.mapSize.width = 256
ghost2.shadow.mapSize.height = 256
ghost2.shadow.camera.far = 8 //10

//Ghost3
ghost3.shadow.mapSize.width = 256
ghost3.shadow.mapSize.height = 256
ghost3.shadow.camera.far = 8 //10

//DoorLight
doorLight.shadow.mapSize.width = 64
doorLight.shadow.mapSize.height = 64
doorLight.shadow.camera.near = 0.5
doorLight.shadow.camera.far = 5
//const doorLightCameraHelper = new THREE.CameraHelper(doorLight.shadow.camera)
//scene.add(doorLightCameraHelper)


/*
    Sky
*/
const sky = new Sky()
sky.scale.set(100, 100, 100)
//sky.scale.setScalar(100)
scene.add(sky)

sky.material.uniforms['turbidity'].value = 10
sky.material.uniforms['rayleigh'].value = 3
sky.material.uniforms['mieCoefficient'].value = 0.1
sky.material.uniforms['mieDirectionalG'].value = 0.95
sky.material.uniforms['sunPosition'].value.set(0.3, -0.038, -0.95)

const sunParams = {
  x: sky.material.uniforms.sunPosition.value.x,
  y: sky.material.uniforms.sunPosition.value.y,
  z: sky.material.uniforms.sunPosition.value.z
}

gui.add(sky.material.uniforms.turbidity, 'value').min(0).max(100).step(1).name('turbidity')
gui.add(sky.material.uniforms.rayleigh, 'value').min(0).max(50).step(0.1).name('rayleigh')
gui.add(sky.material.uniforms.mieCoefficient, 'value').min(0).max(1).step(0.001).name('mieCoefficient')
gui.add(sky.material.uniforms.mieDirectionalG, 'value').min(0).max(1).step(0.001).name('mieDirectionalG')

// Aggiungiamo gli slider
gui.add(sunParams, 'x', -1, 1, 0.001).name('sun x').onChange(val => {
  sky.material.uniforms.sunPosition.value.x = val
})
gui.add(sunParams, 'y', -0.04, 0, 0.00001).name('sun y').onChange(val => {
  sky.material.uniforms.sunPosition.value.y = val
})
gui.add(sunParams, 'z', -1, 1, 0.001).name('sun z').onChange(val => {
  sky.material.uniforms.sunPosition.value.z = val
})


/*
    Fog
*/
scene.fog = new THREE.FogExp2(0x02343f, config.fogDensity)




// KEYBOARD CONTROLS

//  WASD (desktop only with PointerLockControls)
const moveSpeed = 12  
const rotateSpeed = 2  
const verticalSpeed = 2  

const velocity = new THREE.Vector3()
const direction = new THREE.Vector3()
const moveState = {
    forward: false,
    backward: false,
    left: false,
    right: false,
    up: false,
    down: false 
}

const euler = new THREE.Euler(0, 0, 0, 'YXZ')
const PI_2 = Math.PI / 2
const minPolarAngle = 0
const maxPolarAngle = Math.PI


const moveVector = new THREE.Vector3()
const cameraDirection = new THREE.Vector3()

if (!config.isMobile) {
    // Keyboard listener
    const onKeyDown = (event) => {
        switch (event.code) {
            case 'KeyW': case 'ArrowUp':    moveState.forward = true; break
            case 'KeyS': case 'ArrowDown':  moveState.backward = true; break
            case 'KeyA': case 'ArrowLeft':  moveState.left = true; break
            case 'KeyD': case 'ArrowRight': moveState.right = true; break
            case 'ShiftLeft': case 'ShiftRight': moveState.up = true; break      // ← AGGIUNGI
            case 'ControlLeft': case 'ControlRight': moveState.down = true; break // ← AGGIUNGI
        }
    }

    const onKeyUp = (event) => {
        switch (event.code) {
            case 'KeyW': case 'ArrowUp':    moveState.forward = false; break
            case 'KeyS': case 'ArrowDown':  moveState.backward = false; break
            case 'KeyA': case 'ArrowLeft':  moveState.left = false; break
            case 'KeyD': case 'ArrowRight': moveState.right = false; break
            case 'ShiftLeft': case 'ShiftRight': moveState.up = false; break       // ← AGGIUNGI
            case 'ControlLeft': case 'ControlRight': moveState.down = false; break  // ← AGGIUNGI
        }
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('keyup', onKeyUp)
}


const checkCameraPosition = (delta)=>{
    if(!boundingBox){
        return;
    }
    
    if (boundingBox.containsPoint(camera.position)) {
        camera.position.clamp(boundingBox.min, boundingBox.max);
        const center = new THREE.Vector3();
        boundingBox.getCenter(center);
        const pushDirection = new THREE.Vector3().subVectors(camera.position, center).normalize();
        const pushStrength = 3.0; 
        camera.position.addScaledVector(pushDirection, pushStrength * delta);
    }
}


/**
 * Animate
 */
const timer = new Timer()
let frameCount = 0

const tick = () =>
{
    // Timer
    timer.update()
    const delta = timer.getDelta()
    const elapsedTime = timer.getElapsed()
    //console.log(elapsedTime)

    // Movimento camera (solo desktop)
    if (!config.isMobile && controls.isLocked) {
        // Damping solo su Z (movimento avanti/indietro)
        velocity.z -= velocity.z * 10.0 * delta

        // ROTAZIONE con A/D
        if (moveState.left) {
            euler.setFromQuaternion(camera.quaternion)
            euler.y += rotateSpeed * delta
            camera.quaternion.setFromEuler(euler)
        }
        if (moveState.right) {
            euler.setFromQuaternion(camera.quaternion)
            euler.y -= rotateSpeed * delta
            camera.quaternion.setFromEuler(euler)
        }

        // Movimento avanti/indietro
        direction.z = Number(moveState.forward) - Number(moveState.backward)
        if (moveState.forward || moveState.backward) {
            velocity.z -= direction.z * moveSpeed * delta
        }

        // Muovi la camera usando i suoi vettori di direzione
        camera.getWorldDirection(cameraDirection)
        cameraDirection.y = 0  // Mantieni movimento orizzontale
        cameraDirection.normalize()

        moveVector.set(0, 0, 0)  // Reset
        moveVector.addScaledVector(cameraDirection, -velocity.z * delta)  // Solo forward/backward

        camera.position.add(moveVector)

        // Movimento verticale (SHIFT/CTRL)
        if (moveState.up) {
            camera.position.y += verticalSpeed * delta
        }
        if (moveState.down) {
            camera.position.y -= verticalSpeed * delta
        }

        // Opzionale: Limita altezza min/max
        camera.position.y = Math.max(0.5, Math.min(camera.position.y, 10))  // Min 0.5, Max 10


    }

    // Esegui il controllo della posizione DOPO il movimento
    checkCameraPosition(delta)


    // Ghost
    const ghost1Angle=elapsedTime * 0.25
    const ghost1x = Math.cos(ghost1Angle) * 4
    const ghost1z = Math.sin(ghost1Angle) * 4
    const ghost1y = ( Math.sin(ghost1Angle) * Math.sin(ghost1Angle * 2.34) * Math.sin(ghost1Angle * 3.45) / 2 ) + 0.3
    ghost1.position.x = ghost1x
    ghost1.position.z = ghost1z
    ghost1.position.y = ghost1y
    ghostSiluette1.position.x = ghost1x
    ghostSiluette1.position.z = ghost1z
    ghostSiluette1.position.y = ghost1y
    
    
    const ghost2Angle= - elapsedTime * 0.19
    const ghost2x = Math.cos(ghost2Angle) * 5
    const ghost2z = Math.sin(ghost2Angle) * 5
    const ghost2y = ( Math.sin(ghost2Angle) * Math.sin(ghost2Angle * 2.34) * Math.sin(ghost2Angle * 3.45) / 2 ) + 0.3
    ghost2.position.x = ghost2x
    ghost2.position.z = ghost2z
    ghost2.position.y = ghost2y
    ghostSiluette2.position.x = ghost2x
    ghostSiluette2.position.z = ghost2z
    ghostSiluette2.position.y = ghost2y
    

    const ghost3Angle= elapsedTime * 0.12
    const ghost3x = Math.cos(ghost3Angle) * 6
    const ghost3z = Math.sin(ghost3Angle) * 6
    const ghost3y = ( Math.sin(ghost3Angle) * Math.sin(ghost3Angle * 2.34) * Math.sin(ghost3Angle * 3.45) / 2 ) + 0.3
    ghost3.position.x = ghost3x
    ghost3.position.z = ghost3z
    ghost3.position.y = ghost3y
    ghostSiluette3.position.x = ghost3x
    ghostSiluette3.position.z = ghost3z
    ghostSiluette3.position.y = ghost3y

    // Aggiorna visibilità solo ogni 5 frame
    if (frameCount % 5 === 0) {
        graves.children.forEach(child => {
            const distance = camera.position.distanceTo(child.position)
            child.visible = distance < 10
        })
    }

    // Update controls
    if(config.isMobile){
        controls.update()
    }

    /*
    if (frameCount % 60 === 0) {
        console.log(
            `Camera - Pos: (${camera.position.x.toFixed(2)}, ${camera.position.y.toFixed(2)}, ${camera.position.z.toFixed(2)}) ` +
            `Rot: (${camera.rotation.x.toFixed(2)}, ${camera.rotation.y.toFixed(2)}, ${camera.rotation.z.toFixed(2)})`
        )
    }
    */


    frameCount++

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()